import { useEffect, useMemo } from "react";
import {
  MapContainer,
  TileLayer,
  Polyline,
  useMap,
  CircleMarker,
  Circle,
  Tooltip,
  Marker,
} from "react-leaflet";
import AnimatedMarker from "./AnimatedMarker";
import L from "leaflet";
import { Tent } from "lucide-react";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Strip elevation if present: [lat, lng, ele?] -> [lat, lng]
const ll = (p) => [p[0], p[1]];

const POI_TYPE_EMOJI = {
  hazard: "⚠",
  water_source: "💧",
  restaurant: "🍽",
  grocery_store: "🛒",
  bike_repair: "🔧",
  transportation: "🚌",
  scenic_viewpoint: "🔭",
  historical_marker: "🏛",
  lodging: "🛏",
  camping: "🏕",
  other: "📍",
};

const POI_ICON_TO_TYPE = {
  warning: "hazard",
  drop: "water_source",
  forkknife: "restaurant",
  shoppingcart: "grocery_store",
  wrench: "bike_repair",
  car: "transportation",
  bus: "transportation",
  train: "transportation",
  binoculars: "scenic_viewpoint",
  flagbanner: "historical_marker",
  camping: "camping",
  bed: "lodging",
  hotel: "lodging",
  house: "lodging",
  home: "lodging",
  tent: "camping",
  mappin: "other",
};

const normalizeIconName = (icon) =>
  String(icon || "")
    .trim()
    .toLowerCase()
    .replace(/[_\s-]/g, "");

const getPoiEmoji = (poi) => {
  const type = poi?.type;
  if (type && POI_TYPE_EMOJI[type]) return POI_TYPE_EMOJI[type];

  const iconKey = normalizeIconName(poi?.icon);
  const mappedType = POI_ICON_TO_TYPE[iconKey];
  if (mappedType && POI_TYPE_EMOJI[mappedType])
    return POI_TYPE_EMOJI[mappedType];

  return POI_TYPE_EMOJI.other;
};

const poiDivIcon = (emoji, isActive) =>
  L.divIcon({
    className: "",
    html: `<div style="
      width:${isActive ? 30 : 26}px;
      height:${isActive ? 30 : 26}px;
      border-radius:9999px;
      border:2px solid #fff;
      background:${isActive ? "#1A1D1A" : "#C86B4E"};
      display:flex;
      align-items:center;
      justify-content:center;
      box-shadow:0 1px 4px rgba(0,0,0,.35);
      font-size:${isActive ? 15 : 14}px;
      line-height:1;
    ">${emoji}</div>`,
    iconSize: [isActive ? 30 : 26, isActive ? 30 : 26],
    iconAnchor: [isActive ? 15 : 13, isActive ? 15 : 13],
  });

const FlyTo = ({ position, zoom = 12 }) => {
  const map = useMap();
  useEffect(() => {
    if (position && typeof map.setView === "function") {
      map.setView(position, zoom, { animate: false });
    }
  }, [position, zoom, map]);
  return null;
};

const FocusBounds = ({ bounds, maxZoom = 14 }) => {
  const map = useMap();
  useEffect(() => {
    if (!bounds || bounds.length < 2) return;
    map.fitBounds(bounds, {
      padding: [60, 60],
      maxZoom,
      animate: false,
    });
  }, [bounds, maxZoom, map]);
  return null;
};

const FitBounds = ({ points = [] }) => {
  const map = useMap();
  useEffect(() => {
    if (points && points.length > 1)
      map.fitBounds(points, { padding: [30, 30], animate: false });
  }, [points, map]);
  return null;
};

const SyncMapSize = () => {
  const map = useMap();
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      if (typeof map.invalidateSize === "function") {
        map.invalidateSize({ animate: false });
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [map]);
  return null;
};

const ClickHandler = ({ onClick }) => {
  const map = useMap();
  useEffect(() => {
    if (!onClick) return;
    const handler = (e) => onClick({ lat: e.latlng.lat, lng: e.latlng.lng });
    map.on("click", handler);
    return () => map.off("click", handler);
  }, [map, onClick]);
  return null;
};

export default function MapView({
  gpxPoints = [],
  secondaryGpxPoints = [],
  pois = [],
  activePoiId = null,
  activeIndex = null,
  center = null,
  focusPosition = null,
  focusBounds = null,
  highlightFullRoute = false,
  onPoiClick = null,
  onMapClick = null,
  height = "100%",
  interactive = true,
  fit = true,
  // marker props from video playback
  markerPosition = null, // [lat, lng]
  markerRadius = null, // meters
  markerSegment = null, // { start: [lat,lng], end: [lat,lng] }
  highlightPosition = null, // [lat, lng] hover/preview point
  onHoverIndex = null,
}) {
  const latLngPoints = useMemo(() => gpxPoints.map(ll), [gpxPoints]);
  const secondaryLatLngPoints = useMemo(
    () => secondaryGpxPoints.map(ll),
    [secondaryGpxPoints],
  );
  const initialCenter = useMemo(() => {
    if (center) return center;
    // Center between the start and end GPX points (halfway between them)
    if (latLngPoints.length > 1) {
      const start = latLngPoints[0];
      const end = latLngPoints[latLngPoints.length - 1];
      return [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2];
    }
    if (latLngPoints.length === 1) return latLngPoints[0];
    return [46.5, 11.85];
  }, [center, latLngPoints]);

  const traveledPoints = useMemo(() => {
    if (activeIndex == null || latLngPoints.length === 0) return [];
    return latLngPoints.slice(
      0,
      Math.min(activeIndex + 1, latLngPoints.length),
    );
  }, [activeIndex, latLngPoints]);

  const ridingPosition = useMemo(() => {
    if (markerPosition && markerPosition.length === 2) return markerPosition;
    if (activeIndex == null || latLngPoints.length === 0) return null;
    return latLngPoints[Math.min(activeIndex, latLngPoints.length - 1)];
  }, [activeIndex, latLngPoints, markerPosition]);

  const snappedMarkerSection = useMemo(() => {
    if (!markerSegment || latLngPoints.length === 0) {
      return null;
    }

    const hasStart =
      Array.isArray(markerSegment.start) && markerSegment.start.length === 2;
    const hasEnd =
      Array.isArray(markerSegment.end) && markerSegment.end.length === 2;
    if (!hasStart && !hasEnd) return null;

    const nearestIndex = (target) => {
      let bestIdx = 0;
      let bestDist = Infinity;
      for (let i = 0; i < latLngPoints.length; i++) {
        const p = latLngPoints[i];
        const dLat = p[0] - target[0];
        const dLng = p[1] - target[1];
        const d = dLat * dLat + dLng * dLng;
        if (d < bestDist) {
          bestDist = d;
          bestIdx = i;
        }
      }
      return bestIdx;
    };

    const startEqualsEnd =
      hasStart &&
      hasEnd &&
      markerSegment.start[0] === markerSegment.end[0] &&
      markerSegment.start[1] === markerSegment.end[1];

    if (startEqualsEnd) {
      return {
        mode: "location",
        location: [markerSegment.start[0], markerSegment.start[1]],
        points: [],
      };
    }

    if (!hasStart || !hasEnd || latLngPoints.length < 2) {
      const locationTarget = hasStart ? markerSegment.start : markerSegment.end;
      const locationIdx = nearestIndex(locationTarget);
      const locationPoint = latLngPoints[locationIdx];
      return {
        mode: "location",
        location: locationPoint,
        points: [],
      };
    }

    const startIdx = nearestIndex(markerSegment.start);
    const endIdx = nearestIndex(markerSegment.end);
    const from = Math.min(startIdx, endIdx);
    const to = Math.max(startIdx, endIdx);
    const sectionPoints = latLngPoints.slice(from, to + 1);

    if (sectionPoints.length === 0) return null;
    return {
      mode: "segment",
      points: sectionPoints,
      start: latLngPoints[startIdx],
      end: latLngPoints[endIdx],
    };
  }, [latLngPoints, markerSegment]);

  const locationTooltipLabel = useMemo(() => {
    const radius = Number(markerRadius);
    if (!Number.isFinite(radius) || radius <= 0) {
      return "Location";
    }
    return `Location (~${Math.round(radius)}m)`;
  }, [markerRadius]);

  // AnimatedMarker moved to `AnimatedMarker.jsx`

  const handleRouteHover = useMemo(() => {
    if (!onHoverIndex || latLngPoints.length === 0) return null;

    return (e) => {
      const { lat, lng } = e?.latlng || {};
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

      let bestIdx = -1;
      let bestDist = Infinity;
      for (let i = 0; i < latLngPoints.length; i++) {
        const p = latLngPoints[i];
        const dLat = p[0] - lat;
        const dLng = p[1] - lng;
        const d = dLat * dLat + dLng * dLng;
        if (d < bestDist) {
          bestDist = d;
          bestIdx = i;
        }
      }

      if (bestIdx >= 0) onHoverIndex(bestIdx);
    };
  }, [latLngPoints, onHoverIndex]);

  const clearRouteHover = useMemo(() => {
    if (!onHoverIndex) return null;
    return () => onHoverIndex(null);
  }, [onHoverIndex]);

  return (
    <div
      style={{ height, width: "100%" }}
      className="relative"
      data-testid="map-view"
    >
      <MapContainer
        center={initialCenter}
        zoom={12}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={interactive}
        dragging={interactive}
        zoomControl={interactive}
      >
        <SyncMapSize />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {secondaryLatLngPoints.length > 1 && (
          <Polyline
            positions={secondaryLatLngPoints}
            pathOptions={{
              color: "#2C5545",
              weight: 4,
              opacity: 0.25,
              dashArray: "8 10",
            }}
          />
        )}

        {latLngPoints.length > 1 && (
          <Polyline
            positions={latLngPoints}
            pathOptions={{
              color: highlightFullRoute ? "#C86B4E" : "#2C5545",
              weight: highlightFullRoute ? 6 : 4,
              opacity: highlightFullRoute ? 0.95 : 0.55,
              dashArray: activeIndex != null ? "6 8" : null,
            }}
            eventHandlers={
              onHoverIndex
                ? { mousemove: handleRouteHover, mouseout: clearRouteHover }
                : undefined
            }
          />
        )}

        {latLngPoints.length > 1 && onHoverIndex && (
          <Polyline
            positions={latLngPoints}
            pathOptions={{
              color: "#000000",
              weight: 24,
              opacity: 0,
              bubblingMouseEvents: false,
            }}
            eventHandlers={{
              ...(onHoverIndex
                ? { mousemove: handleRouteHover, mouseout: clearRouteHover }
                : {}),
            }}
          />
        )}

        {snappedMarkerSection?.mode === "segment" &&
          snappedMarkerSection.points.length > 1 && (
            <Polyline
              positions={snappedMarkerSection.points}
              pathOptions={{ color: "#C86B4E", weight: 6, opacity: 0.9 }}
            />
          )}

        {traveledPoints.length > 1 && (
          <Polyline
            positions={traveledPoints}
            pathOptions={{ color: "#C86B4E", weight: 5, opacity: 0.95 }}
          />
        )}

        {latLngPoints.length > 0 && (
          <CircleMarker
            center={latLngPoints[0]}
            radius={7}
            pathOptions={{
              color: "white",
              weight: 3,
              fillColor: "#2C5545",
              fillOpacity: 1,
            }}
          />
        )}
        {latLngPoints.length > 1 && (
          <CircleMarker
            center={latLngPoints[latLngPoints.length - 1]}
            radius={7}
            pathOptions={{
              color: "white",
              weight: 3,
              fillColor: "#1A1D1A",
              fillOpacity: 1,
            }}
          />
        )}

        {ridingPosition && !markerRadius && (
          <AnimatedMarker position={ridingPosition}>
            <Tooltip direction="right" offset={[10, 0]} opacity={1} permanent>
              Current
            </Tooltip>
          </AnimatedMarker>
        )}

        {ridingPosition && markerRadius != null && (
          <Circle
            center={ridingPosition}
            radius={markerRadius}
            pathOptions={{
              color: "#C86B4E",
              weight: 2,
              fillColor: "#C86B4E",
              fillOpacity: 0.12,
            }}
          />
        )}

        {pois.map((p) => {
          const isActive = p.id === activePoiId;
          return (
            <Marker
              key={p.id}
              position={[p.lat, p.lng]}
              icon={poiDivIcon(getPoiEmoji(p), isActive)}
              eventHandlers={{
                click: (e) => {
                  e.originalEvent?.stopPropagation();
                  onPoiClick && onPoiClick(p);
                },
              }}
            >
              <Tooltip direction="top" offset={[0, -12]} opacity={0.95}>
                {p.title || "POI"}
              </Tooltip>
            </Marker>
          );
        })}

        {/* Highlighted segment endpoints */}
        {snappedMarkerSection?.mode === "segment" &&
          snappedMarkerSection.start && (
            <CircleMarker
              center={snappedMarkerSection.start}
              radius={6}
              pathOptions={{
                color: "#C86B4E",
                weight: 2,
                fillColor: "white",
                fillOpacity: 1,
              }}
            >
              <Tooltip direction="top" offset={[0, -8]} opacity={1} permanent>
                Start
              </Tooltip>
            </CircleMarker>
          )}
        {snappedMarkerSection?.mode === "segment" &&
          snappedMarkerSection.end && (
            <CircleMarker
              center={snappedMarkerSection.end}
              radius={6}
              pathOptions={{
                color: "#C86B4E",
                weight: 2,
                fillColor: "#1A1D1A",
                fillOpacity: 1,
              }}
            >
              <Tooltip direction="top" offset={[0, -8]} opacity={1} permanent>
                End
              </Tooltip>
            </CircleMarker>
          )}

        {snappedMarkerSection?.mode === "location" &&
          snappedMarkerSection.location && (
            <CircleMarker
              center={snappedMarkerSection.location}
              radius={6}
              pathOptions={{
                color: "#C86B4E",
                weight: 2,
                fillColor: "white",
                fillOpacity: 1,
              }}
            >
              <Tooltip direction="top" offset={[0, -8]} opacity={1} permanent>
                {locationTooltipLabel}
              </Tooltip>
            </CircleMarker>
          )}

        {highlightPosition && highlightPosition.length === 2 && (
          <CircleMarker
            center={highlightPosition}
            radius={8}
            pathOptions={{
              color: "#C86B4E",
              weight: 3,
              fillColor: "#C86B4E",
              fillOpacity: 0.9,
            }}
          >
            <Tooltip direction="top" offset={[0, -8]} opacity={1}>
              Elevation point
            </Tooltip>
          </CircleMarker>
        )}

        {/* ridingPosition handled by AnimatedMarker above when present */}

        {focusBounds && <FocusBounds bounds={focusBounds} />}
        {!focusBounds && (focusPosition || ridingPosition) && (
          <FlyTo position={focusPosition || ridingPosition} zoom={14} />
        )}
        {!focusPosition &&
          !ridingPosition &&
          fit &&
          latLngPoints.length > 1 && <FitBounds points={latLngPoints} />}
        {onMapClick && <ClickHandler onClick={onMapClick} />}
      </MapContainer>
    </div>
  );
}
